import { FormEvent } from 'react';
import { ValidationStringFormat } from './validation/validators';

import { ArrayItemProps as _ArrayItemProps } from './fields/ArrayField';

export type ArrayItemProps = _ArrayItemProps;

export type MessageWithArgs<Args = { [key: string]: any }> = {
  message: string;
  args: Args;
};

export type ErrorMessage = string | MessageWithArgs;

/**
 * Public Form Props
 *
 * Components managed by the Form Generator will receive these props.
 */
export type FormProps = {
  name: string;
  definition: Array<ObjectSchema>;
  errors: Errors<ObjectSchema>;
  touched: Touched<ObjectSchema>;
  values: { [key: string]: unknown };
  setValues: (values: { [key: string]: unknown }) => void;
  submitting: boolean;
  submitted: boolean;
  submissionFailed: boolean;
  setSubmissionFailed: (value: boolean) => void;
  submissionErrors: { [key: string]: ErrorMessage };
  steps: Array<FormStep>;
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
};

export type FormStep = {
  schema: Schema;
  fields: { [key: string]: FieldProps<Schema, any> };
  isValid: () => boolean;
  resetTouched: () => void;
};

/**
 * A <Field > takes these props.
 */
export type FieldProps<
  T extends Schema,
  Value extends SchemaValueType<T> = SchemaValueType<T>
> = {
  schema: T;
  id: string;
  name: string;
  formName?: string;
  value: Value;
  onChange: (e: FieldChangeEvent<SchemaValueType<T>>) => void;
  onBlur: (e: FieldBlurEvent<Touched<T>>) => void;
  touched: Touched<T>;
  errors: Errors<T>;
  forceShowError?: boolean;
  readOnly?: boolean;
  values?: { [key: string]: unknown };
  /**
   * Additional data that may be used by the form fields.
   *
   * Typically used to pass down information surrounding the context of the
   * form (e.g. the name of the currently logged in user).
   */
  extraFieldData?: { [key: string]: unknown };
  isFieldRequired?: boolean;
  resetNextClickCount?: () => void;
};

export type FieldChangeEvent<Value = any> = {
  target: {
    name: string;
    value: Value;
  };
};

// eslint-disable-next-line @typescript-eslint/no-shadow
export type FieldBlurEvent<Touched = any> = {
  target: {
    name: string;
    touched: Touched;
  };
};

// JSON Schema types

export type STBJsonSchema = {
  $uiOptions?: {
    // Which UI component to use. These are defined in WidgetMap in src/resolveFieldComponent.ts
    widget?: string;
    // The label of the empty option when using <select />
    emptySelectOption?: string;
    // Set this to true if you need a value in the schema that is not
    // visible in the forms. (E.g a prepopulated NIN)
    hidden?: boolean;
    // Should the UI component automatically receive focus when mounted?
    // This should often be set to true for text fields that become visible
    // as a result of a choice.
    autoFocus?: boolean;
    // The text to display when using the ObjectCounterField as widget
    totalCountText?: string;
    // Property order is not deterministic in JSON, therefore the UI fields
    // may not always end up in order you'd expect. Use this to force an order.
    propertyOrder?: Array<string>;
    // Input Placeholder
    placeholder?: string;
    // Input autocomplete
    autoComplete?: boolean;
    // text shown under input fields
    hint?: string;
    // Override the default error message keys per field
    errorMessages?: { [key: string]: string };
    // Should the text input field be a multiline input field?
    multiline?: boolean;
    // Number of rows in the text input multiline field
    rows?: number;
    // When widgets need icons for enums
    iconNames?: Array<string>;
    // Can be used if widgets need to display additional enum metadata. Not all widgets implement this.
    enumSubtitles?: Array<string>;
    // page title
    pageTitle?: string;
    // Set styling of submit button
    submitButtonStyle?: 'contained' | 'outlined';
    // change input field size
    fieldSize?: 'xsmall' | 'small' | 'medium' | 'large';
    // change alert tyoe
    alertType?: 'info' | 'success' | 'warning' | 'error';
    // Input field suffix
    suffix?: string;
    // Input field suffix aria label
    suffixAriaLabel?: string;
  };
};

export type StringSchema = STBJsonSchema & {
  type?: 'string';
  title?: string;
  const?: string;
  description?: string;
  enum?: Array<string | null>;
  enumNames?: Array<string>;
  format?: ValidationStringFormat;
  minLength?: number;
  maxLength?: number;
  default?: string | null;
  pattern?: string;
};

export type NumberSchema = STBJsonSchema & {
  type?: 'number';
  title?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  default?: number | null;
  const?: number | null;
};

export type IntegerSchema = STBJsonSchema & {
  type?: 'integer';
  title?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  default?: number | null;
  const?: number | null;
};

export type ObjectSchema<
  Properties extends { [key: string]: Schema } = { [key: string]: Schema }
> = STBJsonSchema & {
  type?: 'object';
  title?: string;
  description?: string;
  properties?: Properties;
  allOf?: Array<ObjectSchema>;
  anyOf?: Array<ObjectSchema>;
  if?: ObjectSchema;
  then?: ObjectSchema;
  else?: ObjectSchema | null;
  required?: Array<string>;
  default?: any;
  const?: any;
};

export type ArraySchema<Items extends Schema = Schema> = STBJsonSchema & {
  type?: 'array';
  title?: string;
  description?: string;
  items: Items;
  minItems?: number;
  maxItems?: number;
  contains?: Schema;
  const?: Array<any>;
  default?: Array<any>;
  not?: Schema;
};

export type BooleanSchema = STBJsonSchema & {
  type?: 'boolean';
  title?: string;
  description?: string;
  enum?: Array<boolean | null>;
  enumNames?: Array<string>;
  default?: boolean | null;
  const?: boolean | null;
};

export type NullSchema = STBJsonSchema & {
  type?: 'null';
  title?: string;
  description?: string;
  default?: null;
  const?: null;
};

// To create a circularly referenced Schema type
interface ObjectSchemaI extends ObjectSchema<{ [key: string]: Schema }> {}
interface ArraySchemaI extends ArraySchema<Schema> {}

export type Schema =
  | StringSchema
  | NumberSchema
  | IntegerSchema
  | ObjectSchemaI
  | ArraySchemaI
  | BooleanSchema
  | NullSchema;

type PrimitiveSchema =
  | StringSchema
  | NumberSchema
  | IntegerSchema
  | BooleanSchema
  | NullSchema;

export type SchemaType = Schema['type'];

// eslint-disable-next-line @typescript-eslint/no-shadow
export type SchemaValueType<Schema> = Schema extends StringSchema
  ? string | null
  : Schema extends NumberSchema
  ? number | null
  : Schema extends IntegerSchema
  ? number | null
  : Schema extends BooleanSchema
  ? boolean | null
  : Schema extends ArraySchema<infer U>
  ? ArraySchemaValue<U>
  : Schema extends ObjectSchema<infer U>
  ? ObjectSchemaValue<U>
  : never;

interface ArraySchemaValue<T extends Schema>
  extends Array<SchemaValueType<T>> {}

type ObjectSchemaValue<T extends { [key: string]: Schema }> = {
  [K in keyof T]: SchemaValueType<T[K]>;
};

interface ArraySchemaErrors<T extends Schema> extends Array<Errors<T>> {}

export type ObjectSchemaErrors<T extends { [key: string]: Schema }> = {
  [K in keyof T]: Errors<T[K]>;
} & { _all: Array<ErrorMessage> };

// eslint-disable-next-line @typescript-eslint/no-shadow
export type Errors<Schema> = Schema extends PrimitiveSchema
  ? Array<ErrorMessage>
  : Schema extends ArraySchema<infer U>
  ? { all: Array<ErrorMessage>; items: ArraySchemaErrors<U> }
  : Schema extends ObjectSchema<infer U>
  ? ObjectSchemaErrors<U>
  : never;

interface ArraySchemaTouched<T extends Schema> extends Array<Touched<T>> {}

type ObjectSchemaTouched<T extends { [key: string]: Schema }> = {
  [K in keyof T]: Touched<T[K]>;
};

// eslint-disable-next-line @typescript-eslint/no-shadow
export type Touched<Schema> = Schema extends PrimitiveSchema
  ? boolean | undefined
  : Schema extends ArraySchema<PrimitiveSchema>
  ? boolean | undefined
  : Schema extends ArraySchema<infer U>
  ? ArraySchemaTouched<U>
  : Schema extends ObjectSchema<infer U>
  ? ObjectSchemaTouched<U>
  : never;

export function isObjectSchema(schema: Schema): schema is ObjectSchema {
  return (
    schema.type === 'object' ||
    !!(schema as any).properties ||
    Array.isArray((schema as any).required)
  );
}

export function isStringSchema(schema: Schema): schema is StringSchema {
  return schema.type === 'string';
}

export function isNumberSchema(schema: Schema): schema is NumberSchema {
  return schema.type === 'number';
}

export function isIntegerSchema(schema: Schema): schema is IntegerSchema {
  return schema.type === 'integer';
}

export function isBooleanSchema(schema: Schema): schema is BooleanSchema {
  return schema.type === 'boolean';
}

export function isArraySchema(schema: Schema): schema is ArraySchema {
  return schema.type === 'array';
}

export function isNullSchema(schema: Schema): schema is NullSchema {
  return schema.type === 'null';
}
